import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authAPI, bookingsAPI, doctorsAPI, emergencyAPI, settingsAPI } from '../services/api';
import { effectiveBookingStatus, enrichAppointmentsWithEffectiveStatus } from '../utils/revenueStats';
import { EMERGENCY_UI_ENABLED } from '../config/hospitalNav';
// Note: Cognito login bypassed · using Lambda-based auth (email + loginPassword via DynamoDB)
// import { cognitoLogin, cognitoLogout } from '../config/cognito';

const AppContext = createContext(null);

// -- Fallback empty chart data ---------------------------------
export const ANALYTICS = {
  dailyBookings: [
    { day:'Mon', bookings:0, revenue:0 }, { day:'Tue', bookings:0, revenue:0 },
    { day:'Wed', bookings:0, revenue:0 }, { day:'Thu', bookings:0, revenue:0 },
    { day:'Fri', bookings:0, revenue:0 }, { day:'Sat', bookings:0, revenue:0 },
    { day:'Sun', bookings:0, revenue:0 },
  ],
  icuUsage:      [{ name:'Occupied', value:0 }, { name:'Available', value:100 }],
  doctorWorkload:[],
  revenueBySpec: [],
};

export const REVENUE = {
  today:0, yesterday:0, thisWeek:0, thisMonth:0, total:0,
  todayGrowth:0, weekGrowth:0, monthGrowth:0,
  monthlyTrend:[],
};

export const BOOKING_STATS = {
  today:0, yesterday:0, thisWeek:0, thisMonth:0,
  todayVsYesterday:0, weekGrowth:0, monthGrowth:0,
};

const INITIAL_BEDS = {
  icuTotal:20, icuOccupied:0,
  generalTotal:80, generalOccupied:0,
  nicuTotal:0, nicuOccupied:0,
  emergencyMode:false, lastUpdated:new Date(),
};

// ═══════════════════════════════════════════════════════════════════
// PRODUCTION FIX: Persistent field cache to prevent intermittent data loss
// Stores the best version of each field we've ever seen for each booking
// ═══════════════════════════════════════════════════════════════════
const BOOKING_CACHE_KEY = 'hcen_booking_field_cache';
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
let _bookingCacheMem = null;

/** Corrupt hcen_user JSON crashed the whole dashboard (ErrorBoundary on every tab). */
function clearStoredSession() {
  ['hcen_user', 'hcen_token', 'hcen_id_token', 'hcen_access_token', 'hcen_refresh_token']
    .forEach(k => { try { localStorage.removeItem(k); } catch {} });
}

function readStoredSession() {
  try {
    const raw = localStorage.getItem('hcen_user');
    if (!raw) return { loggedIn: false, user: null };
    const user = JSON.parse(raw);
    if (!user || typeof user !== 'object') throw new Error('invalid session');
    return { loggedIn: true, user };
  } catch (e) {
    console.warn('[HCEN] Corrupt session cleared — please log in again:', e?.message || e);
    clearStoredSession();
    return { loggedIn: false, user: null };
  }
}

function readJsonArray(key, fallback = []) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function parseEmergencyList(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.emergencies)) return data.emergencies;
  if (Array.isArray(data.Items)) return data.Items;
  return [];
}

const getBookingCache = () => {
  if (_bookingCacheMem) return _bookingCacheMem;
  try {
    const cached = localStorage.getItem(BOOKING_CACHE_KEY);
    if (!cached) { _bookingCacheMem = {}; return _bookingCacheMem; }
    const parsed = JSON.parse(cached);
    const now = Date.now();
    const cleaned = {};
    Object.entries(parsed).forEach(([id, data]) => {
      if (data._cachedAt && (now - data._cachedAt) < CACHE_MAX_AGE_MS) {
        cleaned[id] = data;
      }
    });
    _bookingCacheMem = cleaned;
    return _bookingCacheMem;
  } catch { _bookingCacheMem = {}; return _bookingCacheMem; }
};

const setBookingCache = (id, fields) => {
  try {
    const cache = getBookingCache();
    cache[id] = { ...fields, _cachedAt: Date.now() };
    if (setBookingCache._timer) clearTimeout(setBookingCache._timer);
    setBookingCache._timer = setTimeout(() => {
      try { localStorage.setItem(BOOKING_CACHE_KEY, JSON.stringify(getBookingCache())); } catch {}
      setBookingCache._timer = null;
    }, 800);
  } catch {}
};

/** Cheap fingerprint — skip React updates when poll returns identical data. */
const listFingerprint = (items, idKey = 'id') => {
  if (!items?.length) return '0';
  const last = items[items.length - 1];
  const first = items[0];
  let maxTs = 0;
  let feeSum = 0;
  for (let i = 0; i < items.length; i++) {
    feeSum += Number(items[i].fee || 0);
    const t = items[i]._raw?.updatedAt || items[i]._raw?.checkedInAt
      || items[i]._raw?.createdAt || items[i].bookedAt || 0;
    const n = new Date(t).getTime();
    if (n > maxTs) maxTs = n;
  }
  return `${items.length}:${first[idKey] || first.alertId}:${last[idKey] || last.alertId}:${maxTs}:${feeSum}`;
};

const mergeWithCache = (booking) => {
  const cache = getBookingCache();
  const cached = cache[booking.id];
  const hasSnapName = booking.patientName && booking.patientName !== 'Unknown';

  if (!cached) {
    setBookingCache(booking.id, {
      patientName: booking.patientName,
      age: booking.age,
      gender: booking.gender,
      phone: booking.phone,
      problem: booking.problem,
    });
    return booking;
  }

  const merged = { ...booking };
  let updated = false;

  // Booking snapshot from API always wins — never let stale cache override a real name.
  if (hasSnapName && cached.patientName !== booking.patientName) {
    updated = true;
  } else if (!merged.patientName || merged.patientName === 'Unknown') {
    merged.patientName = cached.patientName;
    updated = true;
  }
  if (!merged.age && cached.age) {
    merged.age = cached.age;
    updated = true;
  }
  if (!merged.gender && cached.gender) {
    merged.gender = cached.gender;
    updated = true;
  }
  if (!merged.phone || merged.phone === '·') {
    merged.phone = cached.phone;
    updated = true;
  }
  if (!merged.problem && cached.problem) {
    merged.problem = cached.problem;
    updated = true;
  }
  
  // Update cache with any new/better values we have now
  if (updated || !cached._cachedAt) {
    setBookingCache(booking.id, {
      patientName: merged.patientName,
      age: merged.age,
      gender: merged.gender,
      phone: merged.phone,
      problem: merged.problem,
    });
  }
  
  return merged;
};

// -- Date helpers ----------------------------------------------
// PRODUCTION FIX (May 11, 2026 · "Today shows yesterday's date" bug):
// Previously used `toISOString().split('T')[0]` which returns UTC date.
// In IST (UTC+5:30) the UTC date is yesterday until 5:30 AM local — so all
// "today / yesterday / week / month" stats were off by a day every night.
const _localDateKey = (d) => {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const todayStr     = () => _localDateKey(new Date());
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate()-1);  return _localDateKey(d); };
const weekAgoStr   = () => { const d = new Date(); d.setDate(d.getDate()-7);  return _localDateKey(d); };
const monthAgoStr  = () => { const d = new Date(); d.setDate(d.getDate()-30); return _localDateKey(d); };
const dayOfWeek    = (dateStr) => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(dateStr).getDay()];

// Helper: build clean phone from all possible DynamoDB field names
const cleanPhone = (b) => {
  const candidates = [
    b.patientPhone, b.phone, b.userPhone, b.mobileNumber,
    b.mobile, b.contactNumber, b.patientMobile, b.contactPhone,
    // HCEN uses phone-based auth · patientId/userId often IS the phone number
    b.patientId, b.userId,
  ].filter(Boolean);

  for (const p of candidates) {
    const s = String(p).replace(/[^0-9+]/g, '').trim();
    if (!s || s === '91' || s === '+91' || s.length < 7) continue;
    // Format: 91XXXXXXXXXX ? +91 XXXXX XXXXX
    const digits = s.replace(/[^0-9]/g, '');
    if (digits.startsWith('91') && digits.length === 12)
      return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
    if (digits.length === 10)
      return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    return s;
  }
  return '·';
};

// Helper: clean age · checks every possible field name
const cleanAge = (b) => {
  // DynamoDB may store age as number or string; try all known field names
  const a = b.patientAge ?? b.age ?? b.userAge ?? b.patientDetails?.age
            ?? b.Age ?? b.userDetails?.age ?? b.patient_age ?? b.user_age ?? null;
  if (a === null || a === undefined || a === '' || a === '·') return null;
  const n = Number(a);
  if (isNaN(n) || n <= 0) return null; // 0 means not provided
  return String(Math.round(n));
};

// Helper: clean gender · checks every possible field name and normalises M/F
const cleanGender = (b) => {
  const g = b.patientGender || b.gender || b.sex
            || b.patientDetails?.gender || b.Gender
            || b.userGender || b.userDetails?.gender || b.patient_gender || '';
  if (!g || g === '·') return null;
  const gl = String(g).toLowerCase().trim();
  if (gl === 'm' || gl === 'male')   return 'Male';
  if (gl === 'f' || gl === 'female') return 'Female';
  if (gl === 'o' || gl === 'other')  return 'Other';
  if (gl.length > 0 && gl !== '·')  return g; // keep unknown but non-empty values
  return null;
};

// -- Normalize raw booking from DynamoDB → UI shape ----------
const normalizeBooking = (b) => ({
  id:             b.bookingId || b.bookingIds || b.id || '',
  // Try every possible name field; never show 'Patient' as the name
  patientName:    b.patientName || b.userName || b.name
                  || b.userDetails?.name || b.patientDetails?.name
                  || b.patient_name || b.user_name
                  || (b.patientPhone ? `Patient ${String(b.patientPhone).slice(-4)}` : null)
                  || (b.phone        ? `Patient ${String(b.phone).slice(-4)}`        : null)
                  || 'Unknown',
  phone:          cleanPhone(b),
  age:            cleanAge(b),
  gender:         cleanGender(b),
  // ADDRESS FIX (May 2026): only trust the address that was captured WITH
  // THIS BOOKING. Falling back to userDetails / patientDetails (legacy
  // enrichment) caused booking #2 to inherit booking #1's address even when
  // the patient never typed it the second time.
  address:        b.patientAddress || b.address || null,
  problem:        b.problem || b.reason || b.symptoms || b.chiefComplaint || null,
  doctor:         b.doctorName || b.doctor || '·',
  specialization: b.specialization || b.department || '·',
  date:           (b.date || b.appointmentDate || '').split('T')[0],
  time:           b.slot || b.appointmentTime || b.time || null,
  status:         mapStatus(b.status),
  type:           b.type || b.bookingType || 'op',
  priority:       b.priority || 'medium',
  fee:            Number(b.consultationFee || b.fee || 0),
  bookedAt:       b.createdAt
                    ? new Date(b.createdAt).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                    : '·',
  _raw: b,      // full raw object including cancelledBy, cancelledByHospital, etc.
});

// Lambda statuses ? website statuses
// New bookings are auto-confirmed · no manual 'pending' step
function mapStatus(s) {
  if (!s) return 'confirmed'; // default: auto-confirmed
  s = s.toLowerCase();
  if (s === 'confirmed') return 'confirmed';
  if (s === 'accepted')  return 'confirmed';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'rejected')  return 'cancelled';
  if (s === 'checked_in' || s === 'checked-in') return 'checked_in';
  if (s === 'completed') return 'completed';
  if (s === 'rescheduled') return 'rescheduled';
  if (s === 'no_show' || s === 'no-show') return 'no_show';
  return 'confirmed';
}



// -- Normalize raw doctor from DynamoDB ? UI shape -------------
const resolveDoctorFee = (d) => {
  const fee = Number(d?.fee);
  const consultationFee = Number(d?.consultationFee);
  if (Number.isFinite(fee) && fee > 0) return fee;
  if (Number.isFinite(consultationFee) && consultationFee > 0) return consultationFee;
  return 500;
};

const normalizeDoctor = (d) => ({
  id:             d.doctorId || d.id,
  name:           d.name || d.doctorName || 'Unknown',
  specialization: d.specialization || d.department || '·',
  available:      d.isAvailable !== undefined ? d.isAvailable : (d.available !== undefined ? d.available : true),
  startTime:      d.startTime || '09:00',
  endTime:        d.endTime || '17:00',
  phone:          d.phone || '·',
  experience:     d.experience || '·',
  patients:       Number(d.totalPatients || d.patients || 0),
  fee:            resolveDoctorFee(d),
  photoUrl:       d.photoUrl || null,
  weeklySlots:    d.weeklySlots || {},   // { Mon:['09:00',...], Tue:[], ... }
  _raw:           d,
});

// -- Booking aggregation contract (May 29 2026) ---------------------------
// Mirror of the Lambda contract in _extract_website/index.js. Two distinct
// concepts that used to be conflated:
//
//   • SCHEDULED OP visit  → real OP appointments (NOT SOS/emergency rows
//                           that the Bookings table ALSO holds), in any
//                           non-cancelled active state. Drives "Bookings"
//                           counts on every card and chart.
//   • EARNED revenue      → only OP rows the patient WALKED INTO
//                           (status = checked_in or completed). Confirmed
//                           but no-show earns ₹0. Same model as Swiggy
//                           recognising revenue at delivery, not at order.
const _isOpBooking          = b => { const t = String(b?.type || b?._raw?.type || '').toLowerCase(); return t !== 'sos' && t !== 'emergency'; };
const _bookingStatus        = b => effectiveBookingStatus(b);
const _isScheduledOpBooking = b => _isOpBooking(b) && ['confirmed','checked_in','accepted','completed'].includes(_bookingStatus(b));
const _isEarnedBooking      = b => _isOpBooking(b) && (_bookingStatus(b) === 'checked_in' || _bookingStatus(b) === 'completed');

// -- Compute stats from raw booking array ----------------------
function computeStats(bookingsRaw, beds, doctors = []) {
  // Defensive frontend filter: even if backend forgets to strip SOS, we do.
  const bookings = (bookingsRaw || []).filter(_isOpBooking);

  // Build doctor → current fee lookup. Bookings store the fee that was active
  // at booking time; if a hospital later updates a doctor's consultation fee,
  // the workload chart needs to reflect the CURRENT fee × patient count so
  // staff don't see ₹1 for a doctor whose card now reads ₹3.
  const docFeeByName = {};
  doctors.forEach(d => {
    if (d && d.name) docFeeByName[d.name] = Number(d.fee) || 0;
  });
  const today     = todayStr();
  const yesterday = yesterdayStr();
  const weekAgo   = weekAgoStr();
  const monthAgo  = monthAgoStr();

  const inRange = (date, from) => date >= from;
  // Revenue uses earned-only (checked_in/completed). Cards = "what we
  // actually pocketed", not "what was on the calendar".
  const earned = bookings.filter(_isEarnedBooking);

  const todayRevenue     = earned.filter(b => b.date === today).reduce((s,b) => s + (b.fee || 0), 0);
  const yesterdayRevenue = earned.filter(b => b.date === yesterday).reduce((s,b) => s + (b.fee || 0), 0);
  const weekRevenue      = earned.filter(b => inRange(b.date, weekAgo)).reduce((s,b) => s + (b.fee || 0), 0);
  const monthRevenue     = earned.filter(b => inRange(b.date, monthAgo)).reduce((s,b) => s + (b.fee || 0), 0);
  const totalRevenue     = earned.reduce((s,b) => s + (b.fee || 0), 0);

  // Booking COUNT cards include any scheduled OP visit (confirmed onwards).
  const todayBookings    = bookings.filter(b => b.date === today     && _isScheduledOpBooking(b)).length;
  const yesterdayBookings= bookings.filter(b => b.date === yesterday && _isScheduledOpBooking(b)).length;
  const weekBookings     = bookings.filter(b => inRange(b.date, weekAgo)  && _isScheduledOpBooking(b)).length;
  const monthBookings    = bookings.filter(b => inRange(b.date, monthAgo) && _isScheduledOpBooking(b)).length;

  // PRODUCTION FIX (May 16, 2026): previously weekGrowth/monthGrowth were
  // hardcoded to 0, so cards always showed "↑ 0% vs last week". Now we
  // compute against the previous rolling window of the same length.
  const _twoWeekAgo  = (() => { const d = new Date(); d.setDate(d.getDate()-14); return _localDateKey(d); })();
  const _twoMonthAgo = (() => { const d = new Date(); d.setDate(d.getDate()-60); return _localDateKey(d); })();
  const inBetween = (date, from, to) => date >= from && date < to; // [from, to)

  const prevWeekRevenue   = earned.filter(b => inBetween(b.date, _twoWeekAgo,  weekAgo)).reduce((s,b)=>s+(b.fee||0),0);
  const prevMonthRevenue  = earned.filter(b => inBetween(b.date, _twoMonthAgo, monthAgo)).reduce((s,b)=>s+(b.fee||0),0);
  const prevWeekBookings  = bookings.filter(b => inBetween(b.date, _twoWeekAgo,  weekAgo)  && _isScheduledOpBooking(b)).length;
  const prevMonthBookings = bookings.filter(b => inBetween(b.date, _twoMonthAgo, monthAgo) && _isScheduledOpBooking(b)).length;

  const pct = (now, prev) => prev === 0 ? (now > 0 ? 100 : 0) : Math.round(((now-prev)/prev)*100);

  // Daily chart (last 7 days). bookings = scheduled OP, revenue = earned only.
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const dailyMap = {};
  days.forEach(d => { dailyMap[d] = { bookings:0, revenue:0 }; });
  bookings.forEach(b => {
    const dow = dayOfWeek(b.date);
    if (!dailyMap[dow]) return;
    if (_isScheduledOpBooking(b)) dailyMap[dow].bookings += 1;
    if (_isEarnedBooking(b))      dailyMap[dow].revenue  += b.fee || 0;
  });

  // -- Monthly Trend · last 6 months from booking dates ----------------------
  const monthlyTrend = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label    = d.toLocaleString('default', { month: 'short', year: '2-digit' });
    const rev = earned.filter(b => b.date && b.date.startsWith(monthStr)).reduce((s, b) => s + (b.fee || 0), 0);
    monthlyTrend.push({ month: label, revenue: rev });
  }

  // -- Doctor workload · grouped from bookings --------------------------------
  // patients = scheduled OP visits the doctor had; revenue = earned only.
  const docMap = {};
  bookings.forEach(b => {
    const doc = b.doctor && b.doctor !== '·' ? b.doctor : null;
    if (!doc) return;
    if (!_isScheduledOpBooking(b)) return;
    if (!docMap[doc]) docMap[doc] = { name: doc, patients: 0, revenue: 0 };
    docMap[doc].patients += 1;
    if (_isEarnedBooking(b)) {
      // Prefer the doctor's CURRENT consultation fee (matches the Doctors
      // page). Fall back to the historical booking fee if the doctor isn't
      // in the current roster (e.g. removed).
      const currentFee = docFeeByName[doc];
      docMap[doc].revenue += (currentFee != null && currentFee > 0)
        ? currentFee
        : (b.fee || 0);
    }
  });
  const doctorWorkload = Object.values(docMap).sort((a, b) => b.patients - a.patients).slice(0, 8);

  // Revenue by specialization (earned only)
  const specMap = {};
  earned.forEach(b => {
    if (!specMap[b.specialization]) specMap[b.specialization] = 0;
    specMap[b.specialization] += (b.fee || 0);
  });

  return {
    revenue: {
      today: todayRevenue, yesterday: yesterdayRevenue,
      thisWeek: weekRevenue, thisMonth: monthRevenue, total: totalRevenue,
      todayGrowth:  pct(todayRevenue, yesterdayRevenue),
      weekGrowth:   pct(weekRevenue,  prevWeekRevenue),
      monthGrowth:  pct(monthRevenue, prevMonthRevenue),
      monthlyTrend,
    },
    bookingStats: {
      today: todayBookings, yesterday: yesterdayBookings,
      thisWeek: weekBookings, thisMonth: monthBookings,
      todayVsYesterday: pct(todayBookings, yesterdayBookings),
      weekGrowth:  pct(weekBookings,  prevWeekBookings),
      monthGrowth: pct(monthBookings, prevMonthBookings),
    },
    analytics: {
      dailyBookings: days.map(d => ({ day:d, ...dailyMap[d] })),
      icuUsage: beds ? [{ name:'Occupied', value:beds.icuOccupied }, { name:'Available', value:beds.icuTotal - beds.icuOccupied }] : [{ name:'Occupied', value:0 }, { name:'Available', value:100 }],
      doctorWorkload,
      revenueBySpec: Object.entries(specMap).map(([name, revenue]) => ({ name, revenue })),
    },
  };
}

export function AppProvider({ children }) {
  // -- Auth state ---------------------------------------------
  const [authBoot] = useState(() => readStoredSession());
  const [isLoggedIn,  setIsLoggedIn]  = useState(authBoot.loggedIn);
  const [currentUser, setCurrentUser] = useState(authBoot.user);

  // -- Core data state ----------------------------------------
  const [beds,          setBeds]          = useState(INITIAL_BEDS);
  const [doctors,       setDoctors]       = useState([]);
  const [appointments,  setAppointments]  = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [toasts,        setToasts]        = useState([]);

  // -- Stats state  -------------------------------------------
  const [revenue,      setRevenue]      = useState(REVENUE);
  const [bookingStats, setBookingStats] = useState(BOOKING_STATS);
  const [analytics,    setAnalytics]   = useState(ANALYTICS);

  // -- Emergency alerts state -----------------------------------
  const [emergencyAlerts,      setEmergencyAlerts]      = useState([]);
  const [emergencyAlertCount,  setEmergencyAlertCount]  = useState(0);

  // -- Loading / error state ----------------------------------
  const [loading,   setLoading]   = useState(false);
  const [dataError, setDataError] = useState(null);

  // -- Ref to hold latest beds without causing dep loop --------
  const bedsRef = useRef(beds);
  const lastFullLoadRef = useRef(0);
  useEffect(() => { bedsRef.current = beds; }, [beds]);

  // -- Toast helper -------------------------------------------
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(p => [...p, { id, message, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);

  // ---------------------------------------------------------
  //  LOAD DATA from AWS after login
  // ---------------------------------------------------------
  const loadAllData = useCallback(async (hospitalId, opts = {}) => {
    if (!hospitalId) return;
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    setDataError(null);

    // -- 25-second timeout — silent fallback, never show banner to hospital
    const timer = setTimeout(() => {
      if (!silent) setLoading(false);
      console.warn('[HCEN] AWS load timeout — showing existing data');
    }, 25000);

    const [bookingsRes, doctorsRes, settingsRes] = await Promise.all([
      bookingsAPI.getByHospital(hospitalId),
      doctorsAPI.getByHospital(hospitalId),
      settingsAPI.get(hospitalId),        // ? load real beds/settings from AWS
    ]);
    const emergencyRes = EMERGENCY_UI_ENABLED
      ? await emergencyAPI.getByHospital(hospitalId)
      : null;

    // -- Doctors (parse first so workload chart can use current fees) -------
    let doctorList = [];
    if (doctorsRes.data) {
      const rawD = Array.isArray(doctorsRes.data) ? doctorsRes.data
                  : doctorsRes.data.doctors || doctorsRes.data.Items || [];
      doctorList = rawD.map(normalizeDoctor);
      setDoctors(doctorList);
    } else if (doctorsRes.error) {
      console.warn('[HCEN] Doctors load error:', doctorsRes.error);
    }

    // -- Bookings -------------------------------------------
    if (bookingsRes.data) {
      const raw = Array.isArray(bookingsRes.data) ? bookingsRes.data
                  : bookingsRes.data.bookings || bookingsRes.data.Items || [];
      // PRODUCTION FIX: Merge with persistent cache to prevent data loss
      const normalized = enrichAppointmentsWithEffectiveStatus(
        raw.map(normalizeBooking).map(mergeWithCache)
      );
      setAppointments(normalized);

      // Compute all stats from bookings (uses current doctor fees for workload)
      const stats = computeStats(normalized, bedsRef.current, doctorList);
      setRevenue(stats.revenue);
      setBookingStats(stats.bookingStats);
      setAnalytics(stats.analytics);

      // Build notifications from recent bookings
      // Use stable IDs (bookingId-based) so read state persists across refreshes
      const readIds = readJsonArray('hcen_read_notifs');
      const recentNotifs = normalized.slice(0, 15).map((b) => ({
        id:      `notif_${b.id || b._raw?.bookingId || b._raw?.bookingIds}`,
        type:    b.status === 'pending' ? 'booking' : (b.status === 'rejected' ? 'cancel' : 'booking'),
        title:   b.status === 'pending' ? 'New Appointment' : `Appointment ${b.status}`,
        message: `${b.patientName} booked with ${b.doctor}`,
        time:    b.bookedAt || 'Recently',
        read:    readIds.includes(`notif_${b.id || b._raw?.bookingId || b._raw?.bookingIds}`),
      }));
      setNotifications(recentNotifs);
    } else if (bookingsRes.error) {
      console.warn('[HCEN] Bookings load error:', bookingsRes.error);
    }

    // -- Emergency Alerts -----------------------------------
    if (EMERGENCY_UI_ENABLED && emergencyRes?.data && !emergencyRes.error) {
      const raw = parseEmergencyList(emergencyRes.data);
      const sorted = raw.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setEmergencyAlerts(sorted);
      setEmergencyAlertCount(sorted.filter(a => (a.status === 'new' || a.status === 'pending')).length);
    } else if (EMERGENCY_UI_ENABLED && emergencyRes?.error) {
      console.warn('[HCEN] Emergency alerts load error:', emergencyRes.error);
    }

    // -- Settings / Beds ------------------------------------
    if (settingsRes?.data && Object.keys(settingsRes.data).length > 0) {
      const s = settingsRes.data?.hospital || settingsRes.data;
      const icuT  = Number(s.icuTotal     || 0);
      const icuO  = Number(s.icuOccupied  || 0);
      const nicuT = Number(s.nicuTotal    || 0);
      const nicuO = Number(s.nicuOccupied || 0);
      const genT  = Math.max(0, Number(s.totalBeds    || 0) - icuT - nicuT);
      const genO  = Math.max(0, Number(s.occupiedBeds || 0) - icuO - nicuO);
      setBeds(p => ({
        ...p,
        icuTotal:        icuT,
        icuOccupied:     icuO,
        nicuTotal:       nicuT,
        nicuOccupied:    nicuO,
        generalTotal:    genT,
        generalOccupied: genO,
        emergencyMode:   s.emergencyMode || false,
        lastUpdated:     s.bedsUpdatedAt ? new Date(s.bedsUpdatedAt) : p.lastUpdated,
      }));

    }

    clearTimeout(timer);
    if (!silent) setLoading(false);
    lastFullLoadRef.current = Date.now();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // -- Refresh session profile (hospital name + staff role) ----
  useEffect(() => {
    if (!isLoggedIn || !localStorage.getItem('hcen_access_token')) return;
    let cancelled = false;
    (async () => {
      const { data } = await authAPI.me();
      if (cancelled || !data?.hospitalId) return;
      setCurrentUser(prev => {
        const hospitalRole = data.role || prev?.hospitalRole || 'owner';
        const hospitalName = data.hospitalName || data.hospital || prev?.hospital || 'HCEN Hospital';
        const staffName    = data.staffName || data.name || prev?.name || '';
        const name = hospitalRole === 'receptionist'
          ? (staffName || prev?.name || 'Receptionist')
          : (staffName || hospitalName || prev?.name);
        if (
          prev?.hospitalId === data.hospitalId &&
          prev?.email === (data.email || prev?.email) &&
          prev?.hospitalRole === hospitalRole &&
          prev?.hospital === hospitalName &&
          prev?.name === name
        ) return prev;
        const updated = {
          ...prev,
          email: data.email || prev?.email,
          hospitalId: data.hospitalId,
          hospitalRole,
          hospital: hospitalName,
          name,
        };
        localStorage.setItem('hcen_user', JSON.stringify(updated));
        return updated;
      });
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  // -- Auto-load data when logged in -------------------------
  useEffect(() => {
    if (isLoggedIn && currentUser?.hospitalId) {
      loadAllData(currentUser.hospitalId);
    }
  }, [isLoggedIn, currentUser?.hospitalId, loadAllData]);

  // -------------------------------------------------------------------------
  //  LIVE REFRESH · single consolidated poll (fast + no request stacking)
  //  - One inFlight guard prevents slow Lambda from queuing 3× parallel calls
  //  - Bookings/doctors/emergency every 2s; beds/settings every ~12s
  //  - Fingerprint diff skips re-render when data unchanged
  //  - refreshData() uses silent mode (no spinner) — auto-poll already updates UI
  // -------------------------------------------------------------------------
  const pollRefs = useRef({ bookingsFp: '', doctorsFp: '', emergencyFp: '', tick: 0, knownPendingIds: new Set(), emergencySeeded: false });

  useEffect(() => {
    if (!isLoggedIn || !currentUser?.hospitalId) return;
    const hId = currentUser.hospitalId;
    let inFlight = false;
    let pollTimer = null;

    const tabHidden = () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const applyEmergencies = (raw) => {
      if (!EMERGENCY_UI_ENABLED) return;
      const sorted = raw.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      const fp = listFingerprint(sorted, 'alertId');
      if (fp === pollRefs.current.emergencyFp) return;
      pollRefs.current.emergencyFp = fp;

      const pending = sorted.filter(a => a.status === 'new' || a.status === 'pending');

      setEmergencyAlerts(sorted);
      setEmergencyAlertCount(pending.length);

      if (!pollRefs.current.emergencySeeded) {
        pending.forEach(a => {
          const id = a.alertId || a.id;
          if (id) pollRefs.current.knownPendingIds.add(id);
        });
        pollRefs.current.emergencySeeded = true;
        return;
      }

      const brandNew = pending.filter(a => {
        const id = a.alertId || a.id;
        return id && !pollRefs.current.knownPendingIds.has(id);
      });
      pending.forEach(a => {
        const id = a.alertId || a.id;
        if (id) pollRefs.current.knownPendingIds.add(id);
      });
      if (brandNew.length > 0) {
        window.dispatchEvent(new CustomEvent('hcen:new-emergency', {
          detail: { count: pending.length, alerts: brandNew, hidden: tabHidden() },
        }));
      }
    };

    const applyBookings = (bRes, freshDoctors) => {
      if (!bRes.data) return;
      const raw = Array.isArray(bRes.data) ? bRes.data : bRes.data.bookings || bRes.data.Items || [];
      const fresh = enrichAppointmentsWithEffectiveStatus(raw.map(normalizeBooking));
      const fp = listFingerprint(fresh);
      if (fp === pollRefs.current.bookingsFp) return;
      pollRefs.current.bookingsFp = fp;

      setAppointments(prev => {
        const prevMap = {};
        prev.forEach(a => { prevMap[a.id] = a; });
        return fresh.map(f => {
          const existing = prevMap[f.id];
          if (!existing) return mergeWithCache(f);
          const merged = { ...f };
          const freshName = f.patientName && f.patientName !== 'Unknown' ? f.patientName : null;
          merged.patientName = freshName || existing.patientName || f.patientName;
          if (!f.age) merged.age = existing.age;
          if (!f.gender) merged.gender = existing.gender;
          if (!f.phone || f.phone === '·') merged.phone = existing.phone;
          if (!f.problem) merged.problem = existing.problem;
          merged._raw = { ...existing._raw, ...f._raw };
          if (existing.status === 'checked_in') {
            const checkedAt = new Date(existing._raw?.checkedInAt || 0).getTime();
            const fresh30s = Date.now() - checkedAt < 30000;
            const serverStillConfirmed = f.status === 'confirmed' || f.status === 'accepted';
            if (fresh30s && serverStillConfirmed) {
              merged.status = 'checked_in';
              merged._raw = { ...merged._raw, checkedInAt: existing._raw?.checkedInAt };
            }
          }
          return mergeWithCache(merged);
        });
      });
      const stats = computeStats(fresh, bedsRef.current, freshDoctors);
      setRevenue(stats.revenue);
      setBookingStats(stats.bookingStats);
      setAnalytics(stats.analytics);
    };

    const poll = async () => {
      if (inFlight) return;
      if (Date.now() - lastFullLoadRef.current < 2000) return;
      inFlight = true;
      pollRefs.current.tick += 1;
      const hidden = tabHidden();
      const fetchSettings = !hidden && pollRefs.current.tick % 6 === 0;

      try {
        if (EMERGENCY_UI_ENABLED) {
          const emPromise = emergencyAPI.getByHospital(hId);

          if (hidden) {
            const emRes = await emPromise;
            if (emRes?.data && !emRes.error) {
              const raw = parseEmergencyList(emRes.data);
              applyEmergencies(raw);
            }
            return;
          }

          const reqs = [emPromise, bookingsAPI.getByHospital(hId), doctorsAPI.getByHospital(hId)];
          if (fetchSettings) reqs.push(settingsAPI.get(hId));
          const [emRes, bRes, dRes, sRes] = await Promise.all(reqs);

          if (emRes?.data && !emRes.error) {
            const raw = parseEmergencyList(emRes.data);
            applyEmergencies(raw);
            if (emRes.data.meta && !emRes.data.meta.sosCapable) {
              console.warn('[HCEN] Hospital has no GPS coordinates — SOS broadcasts cannot reach this dashboard. Contact HCEN support.');
            }
          }

          let freshDoctors = [];
          if (dRes?.data) {
            const rawD = Array.isArray(dRes.data) ? dRes.data : dRes.data.doctors || dRes.data.Items || [];
            freshDoctors = rawD.map(normalizeDoctor);
            const fp = listFingerprint(freshDoctors);
            if (fp !== pollRefs.current.doctorsFp) {
              pollRefs.current.doctorsFp = fp;
              setDoctors(freshDoctors);
            }
          }

          applyBookings(bRes, freshDoctors);

          if (pollRefs.current.tick % 30 === 0) {
            bookingsAPI.sendReminders(hId).catch(() => {});
          }

          if (fetchSettings && sRes?.data && Object.keys(sRes.data).length > 0) {
            const s = sRes.data?.hospital || sRes.data;
            const icuT = Number(s.icuTotal || 0);
            const icuO = Number(s.icuOccupied || 0);
            const nicuT = Number(s.nicuTotal || 0);
            const nicuO = Number(s.nicuOccupied || 0);
            setBeds(p => ({
              ...p,
              icuTotal: icuT,
              icuOccupied: icuO,
              nicuTotal: nicuT,
              nicuOccupied: nicuO,
              generalTotal: Math.max(0, Number(s.totalBeds || 0) - icuT - nicuT),
              generalOccupied: Math.max(0, Number(s.occupiedBeds || 0) - icuO - nicuO),
              emergencyMode: s.emergencyMode || false,
              lastUpdated: s.bedsUpdatedAt ? new Date(s.bedsUpdatedAt) : p.lastUpdated,
            }));
          }
          return;
        }

        if (hidden) return;

        const reqs = [bookingsAPI.getByHospital(hId), doctorsAPI.getByHospital(hId)];
        if (fetchSettings) reqs.push(settingsAPI.get(hId));
        const [bRes, dRes, sRes] = await Promise.all(reqs);

        let freshDoctors = [];
        if (dRes?.data) {
          const rawD = Array.isArray(dRes.data) ? dRes.data : dRes.data.doctors || dRes.data.Items || [];
          freshDoctors = rawD.map(normalizeDoctor);
          const fp = listFingerprint(freshDoctors);
          if (fp !== pollRefs.current.doctorsFp) {
            pollRefs.current.doctorsFp = fp;
            setDoctors(freshDoctors);
          }
        }

        applyBookings(bRes, freshDoctors);

        if (pollRefs.current.tick % 30 === 0) {
          bookingsAPI.sendReminders(hId).catch(() => {});
        }

        if (fetchSettings && sRes?.data && Object.keys(sRes.data).length > 0) {
          const s = sRes.data?.hospital || sRes.data;
          const icuT = Number(s.icuTotal || 0);
          const icuO = Number(s.icuOccupied || 0);
          const nicuT = Number(s.nicuTotal || 0);
          const nicuO = Number(s.nicuOccupied || 0);
          setBeds(p => ({
            ...p,
            icuTotal: icuT,
            icuOccupied: icuO,
            nicuTotal: nicuT,
            nicuOccupied: nicuO,
            generalTotal: Math.max(0, Number(s.totalBeds || 0) - icuT - nicuT),
            generalOccupied: Math.max(0, Number(s.occupiedBeds || 0) - icuO - nicuO),
            emergencyMode: s.emergencyMode || false,
            lastUpdated: s.bedsUpdatedAt ? new Date(s.bedsUpdatedAt) : p.lastUpdated,
          }));
        }
      } finally {
        inFlight = false;
      }
    };

    const schedulePoll = () => {
      clearInterval(pollTimer);
      pollTimer = setInterval(poll, tabHidden() ? 5000 : 3000);
    };

    schedulePoll();

    const onVisibility = () => {
      schedulePoll();
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentUser?.hospitalId]);


  // ---------------------------------------------------------
  //  AUTH
  // ---------------------------------------------------------



  // Helper: persist the result of a successful Cognito (or bcrypt) login.
  const _completeLogin = (email, data) => {
    const tokens = data.tokens || {};
    // Real Cognito access token used as Bearer for all subsequent /hospital/*
    // calls. If absent (legacy bcrypt path) we fall back to the synthetic
    // session token so the UI keeps functioning during rollout.
    const bearer = tokens.accessToken
      || btoa(`hcen_hospital_${data.hospitalId}_${Date.now()}`);
    localStorage.setItem('hcen_token', bearer);
    if (tokens.idToken)      localStorage.setItem('hcen_id_token',      tokens.idToken);
    if (tokens.accessToken)  localStorage.setItem('hcen_access_token',  tokens.accessToken);
    if (tokens.refreshToken) localStorage.setItem('hcen_refresh_token', tokens.refreshToken);

    const hospitalRole = data.role || data.hospitalRole || 'owner';
    const hospitalName = data.hospitalName || data.hospital || data.name || 'HCEN Hospital';
    const staffName    = data.staffName || (hospitalRole === 'receptionist' ? data.name : '') || '';
    const userData = {
      email,
      role:         'hospital',
      hospitalRole,
      name:         staffName || hospitalName || 'Hospital Manager',
      hospital:     hospitalName,
      hospitalId:   data.hospitalId || null,
      city:         data.city       || '',
      phone:        data.phone      || '',
      type:         data.type       || 'Private',
    };
    localStorage.setItem('hcen_user', JSON.stringify(userData));
    setCurrentUser(userData);
    setIsLoggedIn(true);
    return userData;
  };

  const login = useCallback(async (email, password) => {
    try {
      const { data, error } = await authAPI.login(String(email || '').trim().toLowerCase(), password);
      if (error || !data) {
        return { success: false, error: error || 'Invalid email or password' };
      }

      // ── Phase A: Cognito challenge handling ─────────────────────────────
      // Cognito asked the user to set a permanent password (first login or
      // post-migration). Surface this to the caller (Login.js) so it can
      // render the set-password form.
      if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
        return {
          success: false,
          challenge: 'NEW_PASSWORD_REQUIRED',
          session: data.session,
          email:   data.email || email,
        };
      }
      // Lazy-migrated legacy hospital — Cognito user was just created and the
      // Lambda handed back a fresh temp password we must use to immediately
      // complete the sign-in. We treat this as the same NEW_PASSWORD_REQUIRED
      // flow but pass the temp password through transparently.
      if (data.challenge === 'NEW_PASSWORD_REQUIRED_MIGRATION') {
        // Re-issue login with the fresh temp password so Cognito returns a
        // proper session token tied to NEW_PASSWORD_REQUIRED.
        const second = await authAPI.login(String(email || '').trim().toLowerCase(), data.tempPassword);
        if (second.data?.challenge === 'NEW_PASSWORD_REQUIRED') {
          return {
            success: false,
            challenge: 'NEW_PASSWORD_REQUIRED',
            session:   second.data.session,
            email,
          };
        }
        return { success: false, error: 'Migration login failed. Please try again.' };
      }

      const userData = _completeLogin(email, data);
      if (!userData.hospitalId) {
        return { success: false, error: 'Hospital account not found. Contact HCEN admin.' };
      }
      return { success: true };
    } catch (err) {
      console.error('[HCEN] Login error:', err);
      return { success: false, error: 'Login failed. Please try again.' };
    }
  }, []);

  // Used by the "Set new password" screen after a NEW_PASSWORD_REQUIRED
  // challenge. Returns the same { success } shape as login().
  const respondPasswordChallenge = useCallback(async (email, newPassword, session) => {
    try {
      const { data, error } = await authAPI.respondChallenge(email, newPassword, session);
      if (error || !data) return { success: false, error: error || 'Could not set password' };
      _completeLogin(email, data);
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Could not set password. Please try again.' };
    }
  }, []);

  const logout = useCallback(() => {
    // Clear all local session data
    setCurrentUser(null);
    setIsLoggedIn(false);
    setAppointments([]);
    setDoctors([]);
    setRevenue(REVENUE);
    setBookingStats(BOOKING_STATS);
    setAnalytics(ANALYTICS);
    localStorage.removeItem('hcen_user');
    localStorage.removeItem('hcen_token');
    localStorage.removeItem('hcen_id_token');
    localStorage.removeItem('hcen_access_token');
    localStorage.removeItem('hcen_refresh_token');
  }, []);

  // ---------------------------------------------------------
  //  APPOINTMENTS · sync to AWS
  // ---------------------------------------------------------
  const updateAppointment = useCallback(async (id, uiStatus, extraFields = {}) => {
    const prevAppt = appointments.find(a => a.id === id);
    // Optimistic update · merge extraFields into _raw so getCancelledLabel works immediately
    setAppointments(p => p.map(a => a.id === id
      ? { ...a, status: uiStatus, _raw: { ...a._raw, ...extraFields } }
      : a
    ));

    let result;
    if (uiStatus === 'accepted' || uiStatus === 'confirmed') {
      result = await bookingsAPI.confirm(id);
    } else if (uiStatus === 'cancelled' || uiStatus === 'rejected') {
      result = await bookingsAPI.cancel(id, { cancelledBy: extraFields.cancelledBy || 'hospital', ...extraFields });
    } else if (uiStatus === 'rescheduled') {
      result = await bookingsAPI.reschedule(id, extraFields);
    } else {
      result = { error: null };
    }

    if (result?.error) {
      console.warn('[HCEN] Booking sync failed:', result.error);
      if (prevAppt) {
        setAppointments(p => p.map(a => a.id === id ? prevAppt : a));
      }
      showToast('Failed to update appointment: ' + result.error, 'error');
    } else {
      const msg = {
        confirmed:   '✅ Appointment confirmed!',
        accepted:    '✅ Appointment confirmed!',
        cancelled:   '✕ Cancelled by Hospital',
        rejected:    '✕ Cancelled',
        rescheduled: '📅 Appointment rescheduled · patient notified',
      };
      showToast(msg[uiStatus] || 'Status updated', 'success');

      setNotifications(prev => [{
        id:      Date.now(),
        type:    uiStatus === 'confirmed' || uiStatus === 'accepted' ? 'booking' : 'cancel',
        title:   `Appointment ${uiStatus}`,
        message: `Booking #${id} ${uiStatus === 'cancelled' ? 'cancelled by hospital' : 'status: ' + uiStatus}`,
        time:    'just now',
        read:    false,
      }, ...prev.slice(0, 19)]);
    }
  }, [appointments, showToast]);

  // ---------------------------------------------------------
  //  DOCTORS · sync to AWS
  // ---------------------------------------------------------
  const addDoctor = useCallback(async (doctorData) => {
    const hospitalId = currentUser?.hospitalId;
    if (!hospitalId) { showToast('Not logged in', 'error'); return; }

    const payload = {
      hospitalId,
      name:            doctorData.name,
      specialization:  doctorData.specialization  || 'General',
      phone:           doctorData.phone            || '',
      experience:      doctorData.experience       || '',
      consultationFee: Number(doctorData.fee       || doctorData.consultationFee || 500),
      fee:             Number(doctorData.fee       || doctorData.consultationFee || 500),
      startTime:       doctorData.startTime        || '09:00',
      endTime:         doctorData.endTime          || '17:00',
    };

    const { data, error } = await doctorsAPI.add(payload);
    if (error || !data) {
      showToast('Failed to add doctor: ' + (error || 'Unknown error'), 'error');
      return;
    }

    // Normalize to UI shape and add to state
    const newDoc = {
      id:             data.doctorId,
      name:           data.name,
      specialization: data.specialization,
      available:      data.isAvailable !== false,
      startTime:      data.startTime || '09:00',
      endTime:        data.endTime   || '17:00',
      phone:          data.phone     || '·',
      experience:     data.experience|| '·',
      patients:       0,
      fee:            resolveDoctorFee(data),
    };
    setDoctors(p => [...p, newDoc]);
    showToast('Doctor added & saved to AWS ✓', 'success');
  }, [currentUser?.hospitalId, showToast]);

  const updateDoctor = useCallback(async (id, updates) => {
    // Build the Lambda payload · include EVERY field that changed.
    // PATCH /hospital/doctors/{id} saves all body keys to DynamoDB.
    const lambdaUpdates = {};

    // Profile fields
    if (updates.name         !== undefined) lambdaUpdates.name            = updates.name;
    if (updates.specialization !== undefined) lambdaUpdates.specialization = updates.specialization;
    if (updates.phone        !== undefined) lambdaUpdates.phone           = updates.phone;
    if (updates.experience   !== undefined) lambdaUpdates.experience      = updates.experience;
    // Fee · stored as both `fee` and `consultationFee` for compatibility
    if (updates.fee          !== undefined) {
      const feeNum = Number(updates.fee);
      lambdaUpdates.fee             = feeNum;
      lambdaUpdates.consultationFee = feeNum;
    }
    if (updates.startTime    !== undefined) lambdaUpdates.startTime       = updates.startTime;
    if (updates.endTime      !== undefined) lambdaUpdates.endTime         = updates.endTime;
    // Photo · only send if there's actual data (avoid clearing with empty string)
    if (updates.photoUrl && updates.photoUrl.length > 10) lambdaUpdates.photoUrl = updates.photoUrl;
    // Availability · save as BOTH `isAvailable` and `available` so all routes read it correctly
    const avail = updates.available !== undefined ? updates.available : updates.isAvailable;
    if (avail !== undefined) {
      lambdaUpdates.isAvailable = avail;
      lambdaUpdates.available   = avail;
    }
    if (updates.availableSlots) lambdaUpdates.availableSlots = updates.availableSlots;

    // Optimistic UI update — always store numeric fee
    const normalizedUpdates = { ...updates };
    if (updates.fee !== undefined) normalizedUpdates.fee = Number(updates.fee);
    setDoctors(p => p.map(d => d.id === id ? { ...d, ...normalizedUpdates } : d));

    const hospitalId = currentUser?.hospitalId;
    if (hospitalId && !id.startsWith('LOCAL-')) {
      const { error } = await doctorsAPI.update(hospitalId, id, lambdaUpdates);
      if (error) {
        console.warn('[HCEN] Doctor update sync failed:', error);
        showToast('Doctor update saved locally (sync pending)', 'warning');
      } else {
        showToast('Doctor updated! ✓', 'success');
      }
    } else {
      showToast('Doctor updated!', 'success');
    }
  }, [currentUser?.hospitalId, showToast]);

  const deleteDoctor = useCallback(async (id) => {
    // Optimistic update
    setDoctors(p => p.filter(d => d.id !== id));

    if (!id.startsWith('LOCAL-')) {
      const { error } = await doctorsAPI.remove(id);
      if (error) {
        console.warn('[HCEN] Doctor delete sync failed:', error);
        showToast('Removed locally (AWS sync pending)', 'warning');
      } else {
        showToast('Doctor removed from AWS ✓', 'success');
      }
    } else {
      showToast('Doctor removed', 'info');
    }
  }, [showToast]);

  const toggleDoctor = useCallback(async (id) => {
    const doc = doctors.find(d => d.id === id);
    if (!doc) return;
    await updateDoctor(id, { available: !doc.available });
  }, [doctors, updateDoctor]);

  // ---------------------------------------------------------
  //  EMERGENCY ALERTS
  // ---------------------------------------------------------
  const refreshEmergencyAlerts = useCallback(async () => {
    if (!EMERGENCY_UI_ENABLED) return;
    const hospitalId = currentUser?.hospitalId;
    if (!hospitalId) return;
    const { data, error } = await emergencyAPI.getByHospital(hospitalId);
    if (data && !error) {
      const raw = parseEmergencyList(data);
      const sorted = raw.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setEmergencyAlerts(sorted);
      setEmergencyAlertCount(sorted.filter(a => (a.status === 'new' || a.status === 'pending')).length);
    }
  }, [currentUser?.hospitalId]);

  const acknowledgeAlert = useCallback(async (alertId) => {
    if (!EMERGENCY_UI_ENABLED) return;
    // Optimistic update · match by alertId (real DynamoDB key) or id (legacy)
    setEmergencyAlerts(prev => prev.map(a =>
      (a.alertId || a.id) === alertId ? { ...a, status: 'acknowledged' } : a
    ));
    setEmergencyAlertCount(prev => Math.max(0, prev - 1));
    const { error } = await emergencyAPI.acknowledge(alertId, {
      hospitalId:   currentUser?.hospitalId,
      hospitalName: currentUser?.hospital || currentUser?.name,
      hospitalPhone: currentUser?.phone,
    });
    if (error) console.warn('[HCEN] Acknowledge error:', error);
    else showToast('🚨 Emergency acknowledged · team notified!', 'success');
  }, [currentUser, showToast]);

  const handleAlert = useCallback(async (alertId) => {
    if (!EMERGENCY_UI_ENABLED) return;
    setEmergencyAlerts(prev => prev.map(a =>
      (a.alertId || a.id) === alertId ? { ...a, status: 'handled' } : a
    ));
    const { error } = await emergencyAPI.handle(alertId);
    if (error) console.warn('[HCEN] Handle error:', error);
    else showToast('🚨 Emergency marked as handled', 'success');
  }, [showToast]);

  // ---------------------------------------------------------
  //  NOTIFICATIONS · read state persisted to localStorage
  // ---------------------------------------------------------
  const markRead = useCallback((id) => {
    setNotifications(p => p.map(n => n.id === id ? { ...n, read: true } : n));
    const prev = readJsonArray('hcen_read_notifs');
    if (!prev.includes(id)) {
      localStorage.setItem('hcen_read_notifs', JSON.stringify([...prev, id]));
    }
  }, []);
  const markAllRead = useCallback(() => {
    setNotifications(p => {
      const allIds = p.map(n => n.id);
      const prev = readJsonArray('hcen_read_notifs');
      localStorage.setItem('hcen_read_notifs', JSON.stringify([...new Set([...prev, ...allIds])]));
      return p.map(n => ({ ...n, read: true }));
    });
  }, []);
  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  const today = todayStr();
  const todayRevenue = useMemo(
    () => appointments
      .filter(a => a.date === today && _isEarnedBooking(a))
      .reduce((s, a) => s + (a.fee || 0), 0),
    [appointments, today]
  );

  const refreshData = useCallback(
    () => loadAllData(currentUser?.hospitalId, { silent: true }),
    [loadAllData, currentUser?.hospitalId]
  );

  const contextValue = useMemo(() => ({
    isLoggedIn, currentUser, login, logout, respondPasswordChallenge,
    doctors, addDoctor, updateDoctor, deleteDoctor, toggleDoctor,
    appointments, updateAppointment,
    notifications, markRead, markAllRead, unreadCount,
    toasts, showToast,
    analytics, revenue, bookingStats, todayRevenue,
    emergencyAlerts, emergencyAlertCount,
    acknowledgeAlert, handleAlert, refreshEmergencyAlerts,
    loading, dataError,
    refreshData,
  }), [
    isLoggedIn, currentUser, login, logout, respondPasswordChallenge,
    doctors, addDoctor, updateDoctor, deleteDoctor, toggleDoctor,
    appointments, updateAppointment,
    notifications, markRead, markAllRead, unreadCount,
    toasts, showToast,
    analytics, revenue, bookingStats, todayRevenue,
    emergencyAlerts, emergencyAlertCount,
    acknowledgeAlert, handleAlert, refreshEmergencyAlerts,
    loading, dataError,
    refreshData,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
}
